// The chrome every surface shares (KSE-0191): the header (brand, engine
// badge, sync status, surface switcher, account menu) and, on compact
// viewports, the bottom surface bar. Surfaces fill the header's slots — mail
// puts search / compose / a Back button in them, the calendar leaves them
// empty — so the account menu and the Mail | Calendar switch look and behave
// the same everywhere.

import { CalendarDays, Check, ChevronDown, Inbox, KeyRound, Lock, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { StoredAccount } from "../auth";
import { useAuth } from "../auth";
import { NotificationsToggle } from "./NotificationsToggle";
import type { Surface } from "./navigation";

export interface AccountMenuProps {
  accounts: StoredAccount[];
  activeIndex: number;
  username: string;
  onSwitch: (index: number) => void;
  onAdd: () => void;
  onSignOut: () => void;
}

export interface EngineBadge {
  label: "swift-shell" | "mock-shell";
  title: string;
}

/** What AppShell knows and every surface's header shows. */
export interface ChromeProps {
  compact: boolean;
  engineBadge: EngineBadge | null;
  // "Connecting…" / "Syncing…" or null when idle.
  status: string | null;
  surface: Surface;
  calendarAvailable: boolean;
  onSurface: (surface: Surface) => void;
  account: AccountMenuProps;
}

export function AccountSwitcher({
  accounts,
  activeIndex,
  username,
  onSwitch,
  onAdd,
  onSignOut,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${username}`}
        className="flex min-h-11 items-center gap-2 rounded-full border border-border/70 py-1 pl-1 pr-2.5 text-sm transition-colors hover:bg-accent md:min-h-0"
      >
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
        >
          {username.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden max-w-40 truncate md:inline">{username}</span>
        <ChevronDown aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border bg-background p-1.5 shadow-xl"
        >
          {accounts.map((account, index) => (
            <button
              key={`${account.origin}:${account.username}`}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSwitch(index);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{account.username}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {new URL(account.origin).host}
                </span>
              </span>
              {index === activeIndex ? (
                <Check aria-label="Active" className="h-4 w-4 text-primary" />
              ) : null}
            </button>
          ))}
          <div className="my-1 border-t border-border/70" />
          <NotificationsToggle />
          <VaultMenu onDone={() => setOpen(false)} />
          <div className="my-1 border-t border-border/70" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAdd();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent"
          >
            <Plus aria-hidden className="h-4 w-4" />
            Add account
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="w-full rounded-md px-2.5 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
          >
            Sign out of {username}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// The vault entries of the account menu (docs/runbooks/lately-shared-auth.md):
// "Use / add a passkey" while the vault is only unlocked by this browser and
// a platform authenticator exists (reuse-first: an existing lately.org
// passkey — synced from another device — is used, one is created only when
// there is none), the enrolled state plus an explicit "Create a new passkey"
// (replaces the old one everywhere; the menu says so and asks) otherwise,
// and "Lock Lately" — forget the browser session key, so the next start asks
// for the passkey (or, without one, a fresh sign-in).
function VaultMenu({ onDone }: { onDone: () => void }) {
  const { vault, enrollPasskey, lockVault } = useAuth();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (vault.kind !== "ready" || vault.protection === null) {
    return null;
  }
  const canEnroll = vault.protection === "browser" && vault.passkeyAvailable;
  const canReplace = vault.protection === "passkey" && vault.passkeyAvailable;
  const run = (mode: "reuse-or-create" | "create") => {
    setBusy(true);
    setNote(null);
    enrollPasskey({ mode })
      .then((result) => {
        if (mode === "reuse-or-create" && result.reused) {
          setNote("Your existing Lately passkey now unlocks this browser too.");
        } else {
          onDone();
        }
      })
      .catch((cause: unknown) => {
        setNote(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => setBusy(false));
  };
  return (
    <>
      <div className="my-1 border-t border-border/70" />
      {canEnroll ? (
        <button
          type="button"
          role="menuitem"
          disabled={busy}
          onClick={() => run("reuse-or-create")}
          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
        >
          <KeyRound aria-hidden className="h-4 w-4" />
          <span className="min-w-0 flex-1">
            <span className="block">{busy ? "Waiting for your passkey…" : "Use a passkey"}</span>
            <span className="block text-xs text-muted-foreground">
              Touch ID / Face ID unlocks Lately here after a lock. Uses the Lately passkey your
              password manager already has, or creates one.
            </span>
          </span>
        </button>
      ) : (
        <div className="flex items-center gap-2 px-2.5 py-2 text-sm text-muted-foreground">
          <KeyRound aria-hidden className="h-4 w-4" />
          <span className="min-w-0 flex-1 text-xs">
            {vault.protection === "passkey"
              ? "Passkey enrolled — unlocks this browser after a lock. Other devices: sign in once, then choose “Use a passkey”."
              : "Unlocked by this browser only (no passkey support here)"}
          </span>
        </div>
      )}
      {canReplace ? (
        <button
          type="button"
          role="menuitem"
          disabled={busy}
          onClick={() => {
            if (
              !window.confirm(
                "Create a new Lately passkey? It replaces the current one in your password manager: every other device that unlocks Lately with it will ask you to sign in again after its next lock.",
              )
            ) {
              return;
            }
            run("create");
          }}
          className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
        >
          <KeyRound aria-hidden className="h-4 w-4" />
          <span className="min-w-0 flex-1">
            <span className="block">
              {busy ? "Waiting for your passkey…" : "Create a new passkey…"}
            </span>
            <span className="block text-xs text-muted-foreground">
              Replaces the current one — your other devices will need to sign in again
            </span>
          </span>
        </button>
      ) : null}
      {note ? (
        <p role="alert" className="px-2.5 pb-1 text-xs text-muted-foreground">
          {note}
        </p>
      ) : null}
      <button
        type="button"
        role="menuitem"
        disabled={busy}
        onClick={() => {
          onDone();
          void lockVault();
        }}
        className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
      >
        <Lock aria-hidden className="h-4 w-4" />
        <span className="min-w-0 flex-1">
          <span className="block">Lock Lately</span>
          <span className="block text-xs text-muted-foreground">
            {vault.protection === "passkey"
              ? "Next start asks for your passkey"
              : "Next start asks you to sign in again"}
          </span>
        </span>
      </button>
    </>
  );
}

const SURFACES: { surface: Surface; label: string; icon: typeof Inbox }[] = [
  { surface: "mail", label: "Mail", icon: Inbox },
  { surface: "calendar", label: "Calendar", icon: CalendarDays },
];

// Desktop: a segmented control in the header.
function SurfaceSwitcher({
  surface,
  onSurface,
}: {
  surface: Surface;
  onSurface: (surface: Surface) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Surface"
      className="flex shrink-0 rounded-full border border-border/70 bg-muted/40 p-0.5 text-sm"
    >
      {SURFACES.map(({ surface: id, label, icon: Icon }) => {
        const active = id === surface;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            data-surface={id}
            onClick={() => onSurface(id)}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-3 py-1 transition-colors",
              active
                ? "bg-background font-medium text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon aria-hidden className="h-3.5 w-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Compact: the bottom bar. A flex child at the foot of the shell column, so
// it sits inside the body's safe-area padding (above the home indicator)
// without adding its own inset.
export function BottomBar({
  surface,
  onSurface,
}: {
  surface: Surface;
  onSurface: (surface: Surface) => void;
}) {
  return (
    <nav
      aria-label="Surfaces"
      data-bottom-bar
      className="flex h-14 shrink-0 items-stretch border-t border-border/70 bg-background"
    >
      {SURFACES.map(({ surface: id, label, icon: Icon }) => {
        const active = id === surface;
        return (
          <button
            key={id}
            type="button"
            aria-current={active ? "page" : undefined}
            data-surface={id}
            onClick={() => onSurface(id)}
            className={cn(
              "flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
              active ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon aria-hidden className="h-5 w-5" />
            {label}
          </button>
        );
      })}
    </nav>
  );
}

function EngineBadgeChip({ badge }: { badge: EngineBadge }) {
  return (
    <span
      title={badge.title}
      data-engine={badge.label}
      className={
        badge.label === "swift-shell"
          ? "rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-[11px] font-medium text-primary"
          : "rounded-full border border-border bg-muted/50 px-2 py-0.5 font-mono text-[11px] font-medium text-muted-foreground"
      }
    >
      {badge.label}
    </span>
  );
}

/**
 * The header. Desktop: brand · badge · status · `center` (search) · surface
 * switcher · `right` (compose) · account. Compact: `left` (a Back button, or
 * the brand) · `title` · `right` · account — one row, 44px targets; when
 * `expanded` is set (search open) it replaces the row entirely.
 */
export function AppHeader({
  chrome,
  left,
  title,
  center,
  right,
  expanded,
}: {
  chrome: ChromeProps;
  left?: ReactNode;
  title?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  expanded?: ReactNode;
}) {
  const brand = <h1 className="shrink-0 font-display text-xl italic tracking-tight">Lately</h1>;
  const status = chrome.status ? (
    <output className="shrink-0 text-xs text-muted-foreground">{chrome.status}</output>
  ) : null;

  if (chrome.compact) {
    return (
      <header
        data-chrome="compact"
        className="flex min-h-14 items-center gap-2 border-b border-border/70 px-3 py-1.5"
      >
        {expanded ?? (
          <>
            {left ?? brand}
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              {title ? (
                <span className="min-w-0 truncate font-display text-lg tracking-tight">
                  {title}
                </span>
              ) : null}
              {status}
            </div>
            {right}
            <AccountSwitcher {...chrome.account} />
          </>
        )}
      </header>
    );
  }

  return (
    <header
      data-chrome="regular"
      className="flex items-center gap-4 border-b border-border/70 px-4 py-2.5"
    >
      {brand}
      {chrome.engineBadge ? <EngineBadgeChip badge={chrome.engineBadge} /> : null}
      {status}
      <div className="min-w-0 flex-1">{center}</div>
      {chrome.calendarAvailable ? (
        <SurfaceSwitcher surface={chrome.surface} onSurface={chrome.onSurface} />
      ) : null}
      {right}
      <AccountSwitcher {...chrome.account} />
    </header>
  );
}
